import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

export type ItemType = "flights" | "hotels" | "cars" | "cruises" | "activities";

export interface Attachment {
  id: string;
  filename: string;
  content_type: string;
  file_size: number;
  storage_path: string;
  source: "email" | "upload";
  created_at: string;
  trip_id: string | null;
  raw_email_id: string | null;
  item_type: string | null;
  item_index: number | null;
}

export interface RawEmail {
  id: string;
  from_address: string;
  subject: string | null;
  body_text: string | null;
  body_html: string | null;
  received_at: string;
  processed: boolean;
  trip_id: string | null;
}

// Fetch attachments for a specific trip item
export function useItemAttachments(tripId: string | undefined, itemType: ItemType, itemIndex: number) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["attachments", tripId, itemType, itemIndex],
    queryFn: async () => {
      if (!user || !tripId) return [];

      const { data, error } = await supabase
        .from("attachments")
        .select("*")
        .eq("trip_id", tripId)
        .eq("item_type", itemType)
        .eq("item_index", itemIndex)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Attachment[];
    },
    enabled: !!user && !!tripId,
  });
}

// Fetch all attachments for a trip (not linked to specific items)
export function useTripAttachments(tripId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["attachments", tripId],
    queryFn: async () => {
      if (!user || !tripId) return [];

      const { data, error } = await supabase
        .from("attachments")
        .select("*")
        .eq("trip_id", tripId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Attachment[];
    },
    enabled: !!user && !!tripId,
  });
}

// Fetch attachments by source email ID
export function useEmailAttachments(rawEmailId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["attachments", "email", rawEmailId],
    queryFn: async () => {
      if (!user || !rawEmailId) return [];

      const { data, error } = await supabase
        .from("attachments")
        .select("*")
        .eq("raw_email_id", rawEmailId)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as Attachment[];
    },
    enabled: !!user && !!rawEmailId,
  });
}

// Fetch source email by ID
export function useRawEmail(rawEmailId: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["rawEmail", rawEmailId],
    queryFn: async () => {
      if (!user || !rawEmailId) return null;

      const { data, error } = await supabase
        .from("raw_emails")
        .select("*")
        .eq("id", rawEmailId)
        .maybeSingle();

      if (error) throw error;
      return data as RawEmail | null;
    },
    enabled: !!user && !!rawEmailId,
  });
}

// Upload a new attachment
export function useUploadAttachment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      file,
      tripId,
      itemType,
      itemIndex,
    }: {
      file: File;
      tripId: string;
      itemType?: ItemType;
      itemIndex?: number;
    }) => {
      if (!user) throw new Error("Not authenticated");

      // Validate file type
      const allowedTypes = [
        "application/pdf",
        "image/jpeg",
        "image/png",
        "image/gif",
        "image/webp",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      ];

      if (!allowedTypes.includes(file.type)) {
        throw new Error("File type not allowed. Please upload PDF, images, or Word documents.");
      }

      // Validate file size (10MB)
      if (file.size > 10485760) {
        throw new Error("File too large. Maximum size is 10MB.");
      }

      // Generate unique filename
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
      const storagePath = `${user.id}/${tripId}/${timestamp}_${safeName}`;

      // Upload to storage
      const { error: uploadError } = await supabase.storage
        .from("attachments")
        .upload(storagePath, file, {
          contentType: file.type,
          upsert: false,
        });

      if (uploadError) throw uploadError;

      // Create attachment record
      const { data, error: dbError } = await supabase
        .from("attachments")
        .insert({
          user_id: user.id,
          trip_id: tripId,
          item_type: itemType || null,
          item_index: itemIndex ?? null,
          filename: file.name,
          content_type: file.type,
          file_size: file.size,
          storage_path: storagePath,
          source: "upload",
        })
        .select()
        .single();

      if (dbError) {
        // Clean up uploaded file on error
        await supabase.storage.from("attachments").remove([storagePath]);
        throw dbError;
      }

      return data as Attachment;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["attachments", data.trip_id] });
      if (data.item_type && data.item_index !== null) {
        queryClient.invalidateQueries({
          queryKey: ["attachments", data.trip_id, data.item_type, data.item_index],
        });
      }
      toast.success("Attachment uploaded successfully");
    },
    onError: (error) => {
      toast.error("Failed to upload: " + error.message);
    },
  });
}

// Delete an attachment
export function useDeleteAttachment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (attachment: Attachment) => {
      if (!user) throw new Error("Not authenticated");

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from("attachments")
        .remove([attachment.storage_path]);

      if (storageError) {
        console.error("Storage deletion error:", storageError);
        // Continue to delete DB record even if storage fails
      }

      // Delete from database
      const { error: dbError } = await supabase
        .from("attachments")
        .delete()
        .eq("id", attachment.id);

      if (dbError) throw dbError;

      return attachment;
    },
    onSuccess: (attachment) => {
      queryClient.invalidateQueries({ queryKey: ["attachments", attachment.trip_id] });
      if (attachment.item_type && attachment.item_index !== null) {
        queryClient.invalidateQueries({
          queryKey: ["attachments", attachment.trip_id, attachment.item_type, attachment.item_index],
        });
      }
      toast.success("Attachment deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete: " + error.message);
    },
  });
}

// Link/unlink an attachment to/from a specific item
export function useLinkAttachment() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async ({
      attachmentId,
      tripId,
      itemType,
      itemIndex,
    }: {
      attachmentId: string;
      tripId: string;
      itemType: ItemType | null;
      itemIndex: number | null;
    }) => {
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("attachments")
        .update({
          item_type: itemType,
          item_index: itemIndex,
        })
        .eq("id", attachmentId)
        .select()
        .single();

      if (error) throw error;
      return data as Attachment;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["attachments", data.trip_id] });
      toast.success(data.item_type ? "Attachment linked to item" : "Attachment unlinked");
    },
    onError: (error) => {
      toast.error("Failed to update: " + error.message);
    },
  });
}

// Get signed URL for viewing/downloading an attachment
export function useAttachmentUrl(storagePath: string | undefined) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["attachmentUrl", storagePath],
    queryFn: async () => {
      if (!user || !storagePath) return null;

      const { data, error } = await supabase.storage
        .from("attachments")
        .createSignedUrl(storagePath, 3600); // 1 hour expiry

      if (error) throw error;
      return data.signedUrl;
    },
    enabled: !!user && !!storagePath,
    staleTime: 50 * 60 * 1000, // Consider stale after 50 minutes (before URL expires)
  });
}

// Get attachment count for an item (for badge display)
export function useItemAttachmentCount(tripId: string | undefined, itemType: ItemType, itemIndex: number) {
  const { user } = useAuth();

  return useQuery({
    queryKey: ["attachmentCount", tripId, itemType, itemIndex],
    queryFn: async () => {
      if (!user || !tripId) return 0;

      const { count, error } = await supabase
        .from("attachments")
        .select("*", { count: "exact", head: true })
        .eq("trip_id", tripId)
        .eq("item_type", itemType)
        .eq("item_index", itemIndex);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!user && !!tripId,
  });
}
